// SPDX-License-Identifier: AGPL-3.0-or-later
import {shouldShowHdrSettings} from '@app/features/user/components/modals/tabs/appearance_tab/HdrTab';
import {shouldShowAppZoomLevel} from '@app/features/user/components/settings_utils/AppZoomLevelUtils';
import Users from '@app/features/user/state/Users';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
export type UserSettingsTabType =
	| 'my_profile'
	| 'account_security'
	| 'plutonium'
	| 'gift_inventory'
	| 'privacy_safety'
	| 'authorized_apps'
	| 'blocked_users'
	| 'devices'
	| 'appearance'
	| 'accessibility'
	| 'chat_settings'
	| 'voice_video'
	| 'notifications'
	| 'advanced'
	| 'applications'
	| 'component_gallery'
	| 'language'
	| 'keybinds'
	| 'expression_packs'
	| 'linked_accounts';
export interface SectionDefinition {
	id: string;
	tabType: UserSettingsTabType;
	label: MessageDescriptor;
	description?: MessageDescriptor;
	keywords: ReadonlyArray<MessageDescriptor>;
	isAdvanced: boolean;
	isVisible?: () => boolean;
}
export interface SettingsSectionConfig {
	id: string;
	label: string;
	isAdvanced: boolean;
}
export interface SearchableSettingItem {
	id: string;
	tabType: UserSettingsTabType;
	sectionId?: string;
	label: string;
	keywords: Array<string>;
	description?: string;
}
function shouldShowClaimedAccountSections(): boolean {
	return Users.getCurrentUser()?.isClaimed() ?? true;
}
function isSectionVisible(section: SectionDefinition): boolean {
	return !section.isVisible || section.isVisible();
}
function sectionMatchesTab(section: SectionDefinition, tabType: UserSettingsTabType): boolean {
	return section.tabType === tabType;
}
function sectionMatchesAllowedTabs(
	section: SectionDefinition,
	allowedTabTypes: Set<UserSettingsTabType> | undefined,
): boolean {
	return !allowedTabTypes || allowedTabTypes.has(section.tabType);
}
function toSettingsSectionConfig(section: SectionDefinition, i18n: I18n): SettingsSectionConfig {
	return {
		id: section.id,
		label: i18n._(section.label),
		isAdvanced: section.isAdvanced,
	};
}
function toSearchableSettingItem(section: SectionDefinition, i18n: I18n): SearchableSettingItem {
	return {
		id: `section-${section.id}`,
		tabType: section.tabType,
		sectionId: section.id,
		label: i18n._(section.label),
		keywords: section.keywords.map((keyword) => i18n._(keyword)),
		description: section.description ? i18n._(section.description) : undefined,
	};
}
const SECTION_REGISTRY = [
	{
		id: 'theme',
		tabType: 'appearance',
		label: msg`Theme`,
		description: msg`Choose a base appearance, manage Quick CSS, organize saved theme files, and tune theme tokens.`,
		keywords: [
			msg`theme`,
			msg`dark mode`,
			msg`light mode`,
			msg`dark`,
			msg`light`,
			msg`coal`,
			msg`color scheme`,
			msg`system theme`,
			msg`sync`,
			msg`sync theme`,
			msg`theme sync`,
			msg`devices`,
			msg`css`,
			msg`quick css`,
			msg`custom`,
			msg`theme library`,
			msg`theme files`,
			msg`import themes`,
			msg`export themes`,
			msg`theme assets`,
			msg`local files`,
			msg`theme tokens`,
			msg`overrides`,
			msg`colors`,
			msg`fonts`,
			msg`styling`,
		],
		isAdvanced: false,
	},
	{
		id: 'hdr',
		tabType: 'appearance',
		label: msg`High Dynamic Range`,
		description: msg`Control how HDR images are displayed on HDR-capable monitors.`,
		keywords: [msg`hdr`, msg`high dynamic range`, msg`brightness`, msg`color`, msg`display`, msg`tone`],
		isAdvanced: false,
		isVisible: shouldShowHdrSettings,
	},
	{
		id: 'chat-font-scaling',
		tabType: 'appearance',
		label: msg`Chat Font Scaling`,
		description: msg`Adjust the font size in the chat area.`,
		keywords: [msg`font`, msg`size`, msg`text size`, msg`zoom`, msg`scale`, msg`chat font`, msg`chat font scaling`],
		isAdvanced: false,
	},
	{
		id: 'app-zoom-level',
		tabType: 'appearance',
		label: msg`App Zoom Level`,
		description: msg`Adjust the application's zoom level.`,
		keywords: [msg`zoom`, msg`scale`, msg`app zoom`, msg`interface size`, msg`display scaling`],
		isAdvanced: false,
		isVisible: shouldShowAppZoomLevel,
	},
	{
		id: 'messages',
		tabType: 'appearance',
		label: msg`Messages`,
		description: msg`Choose how messages are displayed in chat channels.`,
		keywords: [
			msg`messages`,
			msg`display`,
			msg`compact`,
			msg`cozy`,
			msg`message style`,
			msg`comfy`,
			msg`dense`,
			msg`message layout`,
			msg`display mode`,
			msg`grouping`,
			msg`spacing`,
			msg`timestamps`,
			msg`time`,
			msg`date`,
			msg`message time`,
			msg`avatars`,
			msg`hide avatars`,
			msg`compact mode`,
			msg`user avatars`,
			msg`profile pictures`,
			msg`message groups`,
			msg`space between message groups`,
			msg`gap`,
			msg`space between`,
			msg`message spacing`,
		],
		isAdvanced: false,
	},
	{
		id: 'interface',
		tabType: 'appearance',
		label: msg`Interface`,
		description: msg`Customize interface elements and behaviors.`,
		keywords: [
			msg`sidebar`,
			msg`channel list`,
			msg`navigation`,
			msg`member list`,
			msg`users`,
			msg`members`,
			msg`typing`,
			msg`typing indicator`,
			msg`typing avatars`,
			msg`indicator`,
			msg`who is typing`,
			msg`keyboard`,
			msg`hints`,
			msg`tooltips`,
			msg`shortcut badges`,
			msg`keyboard shortcuts`,
			msg`voice channel`,
			msg`join`,
			msg`double click`,
			msg`double-click`,
			msg`single click`,
			msg`voice join`,
			msg`confirm voice join`,
			msg`voice channel confirmation`,
			msg`two clicks`,
			msg`2 clicks`,
		],
		isAdvanced: false,
	},
	{
		id: 'channel-list',
		tabType: 'appearance',
		label: msg`Channel List`,
		description: msg`Control unread indicator behavior for muted channels in channel lists.`,
		keywords: [
			msg`channel list`,
			msg`muted channels`,
			msg`muted`,
			msg`unread`,
			msg`unread indicator`,
			msg`faded unread`,
			msg`sidebar`,
			msg`favorites`,
		],
		isAdvanced: false,
	},
	{
		id: 'active-now',
		tabType: 'appearance',
		label: msg`Active Now`,
		description: msg`Control how Active Now surfaces across the app.`,
		keywords: [
			msg`active now`,
			msg`activity`,
			msg`presence`,
			msg`online`,
			msg`voice activity`,
			msg`voice`,
			msg`dm sidebar`,
			msg`direct message`,
			msg`friends`,
			msg`join voice`,
			msg`home screen`,
		],
		isAdvanced: false,
	},
	{
		id: 'favorites',
		tabType: 'appearance',
		label: msg`Favorites`,
		description: msg`Control the visibility of favorites throughout the app.`,
		keywords: [msg`favorites`, msg`favorite channels`, msg`starred`, msg`saved`, msg`quick access`],
		isAdvanced: true,
	},
	{
		id: 'visual',
		tabType: 'accessibility',
		label: msg`Visual`,
		keywords: [
			msg`contrast`,
			msg`high contrast`,
			msg`visibility`,
			msg`accessibility`,
			msg`saturation`,
			msg`color`,
			msg`colors`,
			msg`vibrancy`,
			msg`screen reader`,
			msg`a11y`,
			msg`aria`,
			msg`underline`,
			msg`underline links`,
			msg`link underline`,
			msg`text selection`,
			msg`select text`,
			msg`copy text`,
			msg`enable text selection`,
			msg`dm preview`,
			msg`dm previews`,
			msg`message preview`,
			msg`message previews`,
			msg`dm message preview`,
			msg`inbox preview`,
		],
		isAdvanced: false,
	},
	{
		id: 'screen-reader',
		tabType: 'accessibility',
		label: msg`Screen reader`,
		description: msg`Control which chat updates are announced by assistive technology.`,
		keywords: [
			msg`screen reader`,
			msg`a11y`,
			msg`accessibility`,
			msg`announce`,
			msg`announcements`,
			msg`live region`,
			msg`aria live`,
			msg`read messages`,
			msg`read aloud`,
			msg`auto read`,
			msg`autoread`,
			msg`automatic message reading`,
			msg`message announcements`,
			msg`new messages`,
		],
		isAdvanced: false,
	},
	{
		id: 'tts',
		tabType: 'accessibility',
		label: msg`Text-to-speech`,
		keywords: [
			msg`tts`,
			msg`text to speech`,
			msg`speech`,
			msg`narration`,
			msg`read aloud`,
			msg`speech rate`,
			msg`playback speed`,
			msg`tts speed`,
			msg`reading speed`,
			msg`speed`,
			msg`rate`,
		],
		isAdvanced: false,
	},
	{
		id: 'keyboard',
		tabType: 'accessibility',
		label: msg`Keyboard`,
		keywords: [
			msg`keyboard`,
			msg`navigation`,
			msg`shortcuts`,
			msg`focus`,
			msg`focus ring`,
			msg`tab`,
			msg`call`,
			msg`calls`,
			msg`start call`,
			msg`voice call`,
			msg`video call`,
			msg`confirm calls`,
			msg`call confirmation`,
			msg`escape`,
			msg`escape key`,
			msg`esc`,
			msg`keyboard mode`,
			msg`context menu`,
			msg`context menu shortcuts`,
			msg`right click menu`,
			msg`textarea`,
			msg`message input`,
			msg`composer`,
			msg`textarea focus ring`,
		],
		isAdvanced: false,
	},
	{
		id: 'animation',
		tabType: 'accessibility',
		label: msg`Animation`,
		keywords: [
			msg`stickers`,
			msg`animation`,
			msg`animated stickers`,
			msg`gif`,
			msg`emoji`,
			msg`animated emoji`,
			msg`autoplay`,
			msg`auto play`,
		],
		isAdvanced: false,
	},
	{
		id: 'motion',
		tabType: 'accessibility',
		label: msg`Motion`,
		keywords: [
			msg`motion`,
			msg`animation`,
			msg`reduced motion`,
			msg`accessibility`,
			msg`animations`,
			msg`sync motion`,
			msg`sync reduced motion`,
			msg`system motion`,
			msg`system reduced motion`,
			msg`prefers reduced motion`,
		],
		isAdvanced: true,
	},
	{
		id: 'display',
		tabType: 'chat_settings',
		label: msg`Display`,
		keywords: [
			msg`reactions`,
			msg`emoji`,
			msg`react`,
			msg`media display`,
			msg`inline media`,
			msg`embedded media`,
			msg`uploaded media`,
			msg`attachments`,
			msg`link previews`,
			msg`website previews`,
			msg`show embeds`,
			msg`spoilers`,
			msg`spoiler`,
			msg`hide`,
			msg`reveal`,
			msg`moderator`,
			msg`mod`,
			msg`manage messages`,
			msg`channels i moderate`,
		],
		isAdvanced: false,
	},
	{
		id: 'media',
		tabType: 'chat_settings',
		label: msg`Media`,
		keywords: [
			msg`embeds`,
			msg`previews`,
			msg`links`,
			msg`url preview`,
			msg`link preview`,
			msg`media`,
			msg`images`,
			msg`videos`,
			msg`attachments`,
			msg`inline`,
			msg`gif`,
			msg`gifs`,
			msg`gif behavior`,
			msg`gif favorites`,
			msg`auto send gifs`,
			msg`klipy`,
			msg`saved media`,
			msg`media size`,
			msg`media buttons`,
			msg`expiry`,
			msg`expiry indicator`,
			msg`gif indicator`,
		],
		isAdvanced: false,
	},
	{
		id: 'input',
		tabType: 'chat_settings',
		label: msg`Input`,
		keywords: [
			msg`autocomplete`,
			msg`suggestions`,
			msg`emoji picker`,
			msg`mentions`,
			msg`sticker`,
			msg`stickers`,
			msg`expressions`,
			msg`colon`,
			msg`colon autocomplete`,
			msg`expression autocomplete`,
			msg`saved media autocomplete`,
			msg`media button`,
			msg`send button`,
			msg`composer button`,
			msg`composer buttons`,
			msg`message input buttons`,
			msg`toolbar`,
		],
		isAdvanced: false,
	},
	{
		id: 'interaction',
		tabType: 'chat_settings',
		label: msg`Interaction`,
		keywords: [
			msg`typing`,
			msg`indicator`,
			msg`typing status`,
			msg`action bar`,
			msg`message action bar`,
			msg`message hover`,
			msg`hover`,
			msg`quick reactions`,
			msg`shift expand`,
			msg`expand action bar`,
			msg`more button`,
			msg`message buttons`,
		],
		isAdvanced: true,
	},
	{
		id: 'links',
		tabType: 'chat_settings',
		label: msg`External Links`,
		description: msg`Configure how external link warnings are handled.`,
		keywords: [
			msg`links`,
			msg`external links`,
			msg`link warnings`,
			msg`url warnings`,
			msg`external urls`,
			msg`link safety`,
			msg`warning prompts`,
			msg`tracking`,
			msg`tracking parameters`,
			msg`utm`,
			msg`fbclid`,
			msg`gclid`,
			msg`clean url`,
			msg`sanitize url`,
			msg`strip tracking`,
			msg`strip parameters`,
			msg`trust domain`,
			msg`trusted domains`,
			msg`trust all`,
			msg`trust all links`,
		],
		isAdvanced: false,
	},
	{
		id: 'search_engines',
		tabType: 'chat_settings',
		label: msg`Search Engines`,
		description: msg`Configure search engines, translators, and reverse image search providers for selected text and media.`,
		keywords: [
			msg`search`,
			msg`search engine`,
			msg`default search engine`,
			msg`built-in search engines`,
			msg`custom search engines`,
			msg`google`,
			msg`bing`,
			msg`duckduckgo`,
			msg`web search`,
			msg`search with`,
			msg`search selected text`,
			msg`custom search`,
			msg`translator`,
			msg`translators`,
			msg`translate`,
			msg`translation`,
			msg`default translator`,
			msg`custom translators`,
			msg`reverse image`,
			msg`reverse image search`,
			msg`reverse image search providers`,
			msg`image search`,
			msg`search by image`,
			msg`image lookup`,
			msg`saucenao`,
			msg`tineye`,
		],
		isAdvanced: false,
	},
	{
		id: 'sidebar',
		tabType: 'chat_settings',
		label: msg`Sidebar`,
		description: msg`Configure how the community sidebar is displayed.`,
		keywords: [
			msg`sidebar`,
			msg`guild sidebar`,
			msg`community sidebar`,
			msg`channel list`,
			msg`navigation`,
			msg`sidebar display`,
			msg`muted`,
			msg`muted channels`,
			msg`hide muted`,
			msg`hide muted channels`,
		],
		isAdvanced: false,
	},
	{
		id: 'inbox',
		tabType: 'chat_settings',
		label: msg`Inbox`,
		description: msg`Configure how the inbox behaves.`,
		keywords: [
			msg`inbox`,
			msg`mark all as read`,
			msg`mark as read`,
			msg`confirmation`,
			msg`confirm`,
			msg`prompt`,
			msg`unread`,
		],
		isAdvanced: false,
	},
	{
		id: 'audio',
		tabType: 'voice_video',
		label: msg`Audio`,
		description: msg`Configure your microphone, speakers, input mode, processing, and voice sounds.`,
		keywords: [
			msg`audio`,
			msg`voice`,
			msg`microphone`,
			msg`mic`,
			msg`input`,
			msg`audio input`,
			msg`input device`,
			msg`device`,
			msg`speaker`,
			msg`output`,
			msg`audio output`,
			msg`output device`,
			msg`headphones`,
			msg`volume`,
			msg`input volume`,
			msg`output volume`,
			msg`loudness`,
			msg`audio level`,
			msg`screen share audio`,
			msg`screen share audio boost`,
			msg`audio boost`,
			msg`boost`,
			msg`push to talk`,
			msg`ptt`,
			msg`release delay`,
			msg`voice activation`,
			msg`activity threshold`,
			msg`auto sensitivity`,
			msg`keybind`,
			msg`noise`,
			msg`suppression`,
			msg`noise suppression`,
			msg`deepfilter`,
			msg`deepfilternet`,
			msg`echo`,
			msg`echo cancellation`,
			msg`gain control`,
			msg`automatic gain control`,
			msg`background noise`,
			msg`krisp`,
			msg`cancellation`,
			msg`input profile`,
			msg`focused voice`,
			msg`direct input`,
			msg`custom profile`,
			msg`mic test`,
			msg`microphone test`,
			msg`entrance sound`,
			msg`voice entrance sound`,
		],
		isAdvanced: false,
	},
	{
		id: 'video',
		tabType: 'voice_video',
		label: msg`Video`,
		description: msg`Configure your camera, screen sharing quality, previews, and frame rate.`,
		keywords: [
			msg`camera`,
			msg`webcam`,
			msg`video`,
			msg`video input`,
			msg`camera device`,
			msg`preview`,
			msg`video preview`,
			msg`camera preview`,
			msg`camera test`,
			msg`camera effects`,
			msg`background effects`,
			msg`camera quality`,
			msg`resolution`,
			msg`screen sharing quality`,
			msg`screen share quality`,
			msg`stream preview`,
			msg`screen share preview`,
			msg`screen sharing`,
			msg`screen share`,
			msg`streaming`,
			msg`screen sharing codec`,
			msg`codec`,
			msg`picture in picture`,
			msg`pip`,
			msg`popout`,
			msg`disable stream previews`,
			msg`pause preview`,
			msg`pause screen share preview`,
			msg`cursor`,
			msg`show cursor`,
			msg`stream cursor`,
			msg`frame rate`,
			msg`fps`,
			msg`hd`,
			msg`1080p`,
			msg`1440p`,
			msg`source quality`,
			msg`plutonium`,
		],
		isAdvanced: false,
	},
	{
		id: 'diagnostics',
		tabType: 'voice_video',
		label: msg`Diagnostics`,
		description: msg`Upload a voice diagnostics bundle for troubleshooting audio, video, and screen sharing issues.`,
		keywords: [
			msg`diagnostics`,
			msg`voice diagnostics`,
			msg`audio diagnostics`,
			msg`video diagnostics`,
			msg`screen share diagnostics`,
			msg`troubleshooting`,
			msg`support`,
			msg`logs`,
			msg`debug logs`,
			msg`upload diagnostics`,
			msg`export diagnostics`,
			msg`call logs`,
			msg`voice logs`,
		],
		isAdvanced: false,
	},
	{
		id: 'connections',
		tabType: 'privacy_safety',
		label: msg`Connections`,
		keywords: [
			msg`friend requests`,
			msg`friend request`,
			msg`who can friend`,
			msg`friends`,
			msg`add friend`,
			msg`friends of friends`,
			msg`mutual friends`,
			msg`community members`,
			msg`mutual guilds`,
			msg`direct messages`,
			msg`dm`,
			msg`dm permissions`,
			msg`who can dm`,
			msg`bot dm`,
			msg`community dm`,
			msg`incoming dm`,
		],
		isAdvanced: false,
	},
	{
		id: 'communication',
		tabType: 'privacy_safety',
		label: msg`Communication`,
		keywords: [
			msg`dm`,
			msg`direct message`,
			msg`privacy`,
			msg`who can dm`,
			msg`friends`,
			msg`requests`,
			msg`friend requests`,
			msg`incoming calls`,
			msg`who can call`,
			msg`call permissions`,
			msg`block calls`,
			msg`friends only calls`,
			msg`call privacy`,
			msg`silent`,
			msg`silent calls`,
			msg`ring behavior`,
			msg`mute calls`,
			msg`no ring`,
			msg`group chat`,
			msg`group dm`,
			msg`add to group`,
			msg`who can add`,
			msg`group permissions`,
			msg`group privacy`,
			msg`friends of friends`,
			msg`mutual friends`,
			msg`extended network`,
			msg`community`,
			msg`guild members`,
			msg`community members`,
			msg`community members`,
		],
		isAdvanced: false,
	},
	{
		id: 'mention-preference',
		tabType: 'privacy_safety',
		label: msg`Mention Preference`,
		keywords: [
			msg`mention`,
			msg`mentions`,
			msg`reply`,
			msg`replies`,
			msg`reply mention`,
			msg`@mention`,
			msg`ping`,
			msg`ping on reply`,
			msg`notification`,
			msg`notify on reply`,
		],
		isAdvanced: false,
	},
	{
		id: 'sensitive-content',
		tabType: 'privacy_safety',
		label: msg`Sensitive Content`,
		description: msg`Control how mature or sensitive media is filtered in different contexts.`,
		keywords: [
			msg`sensitive content`,
			msg`sensitive media`,
			msg`mature content`,
			msg`mature media`,
			msg`filter`,
			msg`filtering`,
			msg`blur`,
			msg`media filter`,
			msg`content filter`,
			msg`privacy`,
			msg`nsfw`,
			msg`adult`,
			msg`adult content`,
			msg`age restricted`,
			msg`18+`,
		],
		isAdvanced: false,
	},
	{
		id: 'data-export',
		tabType: 'privacy_safety',
		label: msg`Data Export`,
		keywords: [msg`export`, msg`data`, msg`download`, msg`gdpr`, msg`harvest`, msg`my data`, msg`account data`],
		isAdvanced: true,
	},
	{
		id: 'data-deletion',
		tabType: 'privacy_safety',
		label: msg`Data Deletion`,
		keywords: [
			msg`delete`,
			msg`data`,
			msg`privacy`,
			msg`gdpr`,
			msg`remove`,
			msg`bulk delete`,
			msg`delete messages`,
			msg`delete all messages`,
			msg`purge`,
			msg`purge messages`,
			msg`message deletion`,
		],
		isAdvanced: true,
	},
	{
		id: 'account',
		tabType: 'account_security',
		label: msg`Account`,
		keywords: [msg`email`, msg`mail`, msg`address`, msg`contact`],
		isAdvanced: false,
		isVisible: shouldShowClaimedAccountSections,
	},
	{
		id: 'security',
		tabType: 'account_security',
		label: msg`Security`,
		keywords: [
			msg`password`,
			msg`credentials`,
			msg`login`,
			msg`security`,
			msg`change password`,
			msg`2fa`,
			msg`two factor`,
			msg`mfa`,
			msg`authentication`,
			msg`authenticator`,
			msg`totp`,
			msg`otp`,
			msg`backup codes`,
			msg`recovery`,
			msg`codes`,
			msg`2fa backup`,
			msg`passkey`,
			msg`passkeys`,
			msg`webauthn`,
			msg`passwordless`,
			msg`face id`,
			msg`touch id`,
			msg`biometric`,
			msg`biometrics`,
			msg`fingerprint`,
			msg`security key`,
			msg`yubikey`,
		],
		isAdvanced: false,
		isVisible: shouldShowClaimedAccountSections,
	},
	{
		id: 'danger_zone',
		tabType: 'account_security',
		label: msg`Danger Zone`,
		keywords: [msg`delete`, msg`remove`, msg`close account`, msg`account removal`, msg`permanent deletion`],
		isAdvanced: false,
	},
	{
		id: 'notifications',
		tabType: 'notifications',
		label: msg`Notifications`,
		keywords: [
			msg`notifications`,
			msg`desktop`,
			msg`alerts`,
			msg`popup`,
			msg`toast`,
			msg`mentions`,
			msg`ping`,
			msg`@`,
			msg`notify`,
			msg`dm`,
			msg`direct message`,
			msg`private message`,
			msg`unread`,
			msg`unread badge`,
			msg`unread message badge`,
			msg`app icon badge`,
			msg`message badge`,
			msg`dock badge`,
			msg`taskbar badge`,
			msg`browser notifications`,
			msg`enable notifications`,
			msg`push subscription`,
		],
		isAdvanced: false,
	},
	{
		id: 'sounds',
		tabType: 'notifications',
		label: msg`Sounds`,
		keywords: [
			msg`sounds`,
			msg`audio`,
			msg`notification sound`,
			msg`alert sound`,
			msg`mute`,
			msg`message sound`,
			msg`sound effect`,
			msg`new message`,
			msg`call`,
			msg`ring`,
			msg`ringtone`,
			msg`incoming call`,
			msg`disable sounds`,
			msg`mute all`,
			msg`silence`,
			msg`quiet`,
			msg`mute notifications`,
			msg`custom sounds`,
			msg`upload sound`,
			msg`custom notification`,
			msg`custom ringtone`,
			msg`mp3`,
			msg`wav`,
			msg`plutonium`,
			msg`volume`,
			msg`master volume`,
			msg`sound volume`,
			msg`output volume`,
			msg`per sound volume`,
			msg`deafen`,
			msg`voice deafen`,
			msg`stream`,
			msg`screen share sound`,
		],
		isAdvanced: false,
	},
	{
		id: 'text-to-speech',
		tabType: 'notifications',
		label: msg`Text-to-speech`,
		keywords: [
			msg`tts`,
			msg`text to speech`,
			msg`speech`,
			msg`narration`,
			msg`read aloud`,
			msg`accessibility`,
			msg`/tts`,
			msg`tts command`,
			msg`speech command`,
			msg`playback`,
			msg`auto narration`,
			msg`automatic`,
			msg`speak messages`,
			msg`read messages`,
			msg`narrate`,
		],
		isAdvanced: false,
	},
	{
		id: 'push',
		tabType: 'notifications',
		label: msg`Push Settings`,
		keywords: [
			msg`push`,
			msg`mobile`,
			msg`push notifications`,
			msg`afk`,
			msg`afk timeout`,
			msg`idle`,
			msg`idle timeout`,
			msg`away`,
			msg`inactive`,
			msg`inactive timeout`,
			msg`mobile push timeout`,
			msg`push timeout`,
		],
		isAdvanced: false,
	},
	{
		id: 'buttons',
		tabType: 'component_gallery',
		label: msg`Buttons`,
		keywords: [msg`buttons`, msg`button`, msg`click`, msg`action`],
		isAdvanced: false,
	},
	{
		id: 'inputs',
		tabType: 'component_gallery',
		label: msg`Inputs & Text`,
		keywords: [msg`inputs`, msg`text`, msg`input field`, msg`text field`, msg`form`],
		isAdvanced: false,
	},
	{
		id: 'selections',
		tabType: 'component_gallery',
		label: msg`Selections`,
		keywords: [msg`selections`, msg`select`, msg`dropdown`, msg`checkbox`, msg`radio`, msg`switch`, msg`toggle`],
		isAdvanced: false,
	},
	{
		id: 'overlays',
		tabType: 'component_gallery',
		label: msg`Overlays & Menus`,
		keywords: [msg`overlays`, msg`menus`, msg`modal`, msg`popup`, msg`context menu`, msg`dropdown menu`],
		isAdvanced: false,
	},
	{
		id: 'indicators',
		tabType: 'component_gallery',
		label: msg`Indicators & Status`,
		keywords: [msg`indicators`, msg`status`, msg`loading`, msg`spinner`, msg`progress`, msg`badge`],
		isAdvanced: false,
	},
	{
		id: 'status',
		tabType: 'component_gallery',
		label: msg`Status Slate`,
		keywords: [msg`status slate`, msg`empty state`, msg`error state`, msg`placeholder`],
		isAdvanced: false,
	},
	{
		id: 'typography',
		tabType: 'component_gallery',
		label: msg`Typography`,
		keywords: [msg`typography`, msg`fonts`, msg`text styles`, msg`type scale`],
		isAdvanced: false,
	},
	{
		id: 'markdown',
		tabType: 'component_gallery',
		label: msg`Markdown`,
		keywords: [msg`markdown`, msg`formatting`, msg`rich text`, msg`md`],
		isAdvanced: false,
	},
	{
		id: 'messages',
		tabType: 'component_gallery',
		label: msg`Messages`,
		keywords: [msg`messages`, msg`chat`, msg`message component`, msg`message display`],
		isAdvanced: false,
	},
] as const satisfies ReadonlyArray<SectionDefinition>;
export type SettingsSectionId = (typeof SECTION_REGISTRY)[number]['id'];
export type SettingsSectionDefinition = (typeof SECTION_REGISTRY)[number];
export function getSectionsForTab(tabType: UserSettingsTabType, i18n: I18n): Array<SettingsSectionConfig> {
	return getVisibleSectionsForTab(tabType).map((section) => toSettingsSectionConfig(section, i18n));
}
export function getSectionIdsForTab(tabType: UserSettingsTabType): Array<string> {
	return getVisibleSectionsForTab(tabType).map((section) => section.id);
}
export function tabHasSections(tabType: UserSettingsTabType): boolean {
	return SECTION_REGISTRY.some((section) => section.tabType === tabType);
}
export function getAllSectionDefinitions(): Array<SettingsSectionDefinition> {
	return Array.from(SECTION_REGISTRY);
}
export function getSectionDefinition(sectionId: string): SettingsSectionDefinition | undefined {
	return SECTION_REGISTRY.find((section) => section.id === sectionId);
}
export function getVisibleSectionsForTab(tabType: UserSettingsTabType): Array<SettingsSectionDefinition> {
	return SECTION_REGISTRY.filter((section) => sectionMatchesTab(section, tabType) && isSectionVisible(section));
}
export function isSectionIdValid(sectionId: string, tabType?: UserSettingsTabType): boolean {
	return SECTION_REGISTRY.some((section) => {
		if (section.id !== sectionId) return false;
		if (tabType && section.tabType !== tabType) return false;
		return true;
	});
}
export function getSearchableItemsFromRegistry(
	i18n: I18n,
	allowedTabTypes?: Set<UserSettingsTabType>,
): Array<SearchableSettingItem> {
	return SECTION_REGISTRY.filter(
		(section) => sectionMatchesAllowedTabs(section, allowedTabTypes) && isSectionVisible(section),
	).map((section) => toSearchableSettingItem(section, i18n));
}
